import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  InputAdornment,
} from "@mui/material";
import { Lock } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useUrlParams } from "hooks/useUrlParams";
import AppLayout from "works/higurashi/components/AppLayout";
import ButtonDialog from "default/components/ButtonDialog";
import { useTheme } from "@emotion/react";
import StyledButton from "default/components/StyledButton";
import Loading from "default/components/Loading";
import { getUserEmail, updateUserEmail } from "utils/api";

function EmailSettings() {
  const { work } = useUrlParams();
  const { t } = useTranslation(["common", work]);
  const theme = useTheme();

  const [currentEmail, setCurrentEmail] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [doNotReceiveNotifications, setDoNotReceiveNotifications] =
    useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [emailError, setEmailError] = useState("");

  // メールアドレスの形式バリデーション
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // 初回ロード時
  useEffect(() => {
    const fetchCurrentEmail = async () => {
      try {
        const emailData = await getUserEmail();
        setCurrentEmail(emailData.email_address);
        setDoNotReceiveNotifications(!emailData.mail_delivery_enabled);
      } catch (error) {
        console.error("Failed to fetch current email:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCurrentEmail();
  }, []);

  // メール設定更新処理
  const handleUpdateEmail = async () => {
    // メールアドレスが入力されている場合のみバリデーション
    if (newEmail && !validateEmail(newEmail)) {
      setEmailError(t("common:EmailSettings.invalid_email_format"));
      return;
    }

    setEmailError("");
    setIsProcessing(true);
    try {
      // メールアドレスが空の場合はnullまたは空文字を送信
      const emailToSend = newEmail || null;
      await updateUserEmail(emailToSend, doNotReceiveNotifications);

      // 最新情報を取得して状態を更新
      const updatedEmailData = await getUserEmail();
      setCurrentEmail(updatedEmailData.email_address);
      setDoNotReceiveNotifications(!updatedEmailData.mail_delivery_enabled);
      setNewEmail("");

      setDialogMessage(t("common:EmailSettings.update_success"));
      setOpenDialog(true);
    } catch (error) {
      console.error("Failed to update email:", error);
      setDialogMessage(t("common:EmailSettings.update_fail"));
      setOpenDialog(true);
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <AppLayout page="EmailSettings">
        <Loading />
      </AppLayout>
    );
  }

  return (
    <AppLayout page="EmailSettings">
      <Box
        sx={{
          display: "flex",
          paddingTop: 4,
          paddingBottom: 4,
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 1,
            marginBottom: 4,
          }}
        >
          <Typography sx={(theme) => ({ ...theme.components.pageHeader })}>
            E-mail Settings
          </Typography>
        </Box>

        {/* 現在のメールアドレス */}
        <Box sx={{ width: "100%", maxWidth: 400, marginBottom: 3 }}>
          <TextField
            fullWidth
            variant="outlined"
            value={currentEmail}
            disabled
            label={t("common:EmailSettings.current_email")}
            placeholder={t("common:EmailSettings.current_email")}
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Lock sx={{ color: theme.system.colors.text.primary }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: `${theme.system.colors.text.primary}`,
                color: `${theme.system.colors.text.primary}`,
                fontFamily: theme.system.fonts.body,
              },
              "& .MuiOutlinedInput-root.Mui-disabled": {
                backgroundColor: `${theme.system.colors.text.primary}20`,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.system.colors.text.primary,
                },
              },
              "& .MuiInputLabel-root.Mui-disabled": {
                color: theme.system.colors.text.primary,
                fontFamily: theme.system.fonts.body,
              },
            }}
          />
        </Box>

        {/* 新しいメールアドレス */}
        <Box sx={{ width: "100%", maxWidth: 400, marginBottom: 3 }}>
          <TextField
            fullWidth
            variant="outlined"
            value={newEmail}
            onChange={(e) => {
              setNewEmail(e.target.value);
              if (emailError) setEmailError("");
            }}
            placeholder={t("common:EmailSettings.new_email")}
            error={!!emailError}
            helperText={emailError}
            sx={{
              "& .MuiInputBase-input": {
                color: theme.system.colors.text.primary,
                fontFamily: theme.system.fonts.body,
              },
              "& .MuiOutlinedInput-root": {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: emailError
                    ? "error.main"
                    : theme.system.colors.text.primary,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: emailError
                    ? "error.main"
                    : theme.system.colors.text.primary,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: emailError
                    ? "error.main"
                    : theme.system.colors.text.primary,
                },
              },
              "& .MuiInputBase-input::placeholder": {
                color: theme.system.colors.text.primary,
                opacity: 0.7,
                fontFamily: theme.system.fonts.body,
              },
              "& .MuiFormHelperText-root": {
                color: "error.main",
                fontFamily: theme.system.fonts.body,
              },
            }}
          />
        </Box>

        {/* お知らせメールを受け取らない */}
        <Box sx={{ width: "100%", maxWidth: 400, marginBottom: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={doNotReceiveNotifications}
                onChange={(e) => setDoNotReceiveNotifications(e.target.checked)}
                sx={{
                  color: theme.system.colors.text.secondary,
                  "&.Mui-checked": {
                    color: theme.system.colors.text.secondary,
                  },
                }}
              />
            }
            label={t("common:EmailSettings.do_not_receive_notifications")}
            sx={{
              color: theme.system.colors.text.secondary,
              fontFamily: theme.system.fonts.body,
            }}
          />
        </Box>

        {/* 4. 変更ボタン */}
        <StyledButton
          variant="contained"
          onClick={handleUpdateEmail}
          disabled={isProcessing}
          sx={{
            marginTop: 4,
            background: isProcessing ? "white" : "transparent",
          }}
        >
          {t("common:button.update")}
        </StyledButton>
      </Box>
      <ButtonDialog
        open={openDialog}
        title={dialogMessage}
        buttons={[
          {
            label: `${t("common:button.close")}`,
            onClick: () => {
              setOpenDialog(false);
            },
          },
        ]}
        handleClose={() => {
          setOpenDialog(false);
        }}
      />
    </AppLayout>
  );
}

export default EmailSettings;
